<template>
  <div class="customer-service-bot-mobile">
    <div
      v-show="!menuVisible"
      ref="floatingRef"
      class="mobile-floating-service"
      :class="{ 'is-dragging': isDragging }"
      :style="floatingPositionStyle"
      @mousedown="startDrag"
      @touchstart="startTouchDrag"
    >
      <button type="button" class="mobile-floating-service__avatar" @click.stop="showMenu">
        <img :src="withBase('/images/chatbot/avatar.png')" alt="客服" />
      </button>

      <BaseBackToTop
        class="mobile-floating-service__back-top"
        inline
        :visible="showBackToTop"
        :size="40"
        :icon-size="24"
        :guard="canUseFloatingAction"
      />
    </div>

    <Teleport to="body">
      <MobilePopup
        v-model:visible="menuVisible"
        title="客户服务"
        panel-class="mobile-service-drawer"
        height="auto"
        @close="hideMenu"
      >
        <div class="mobile-service-menu">
          <ContactPopup>
            <template #trigger>
              <div class="mobile-service-menu__item">
                <SvgIcon class="mobile-service-menu__icon" name="dialog" size="20" />
                <span class="mobile-service-menu__content">
                  <span class="mobile-service-menu__title">在线咨询</span>
                  <span class="mobile-service-menu__desc">
                    产品选型、方案定制、售后支持，一站式获取专家解答
                  </span>
                </span>
                <IconRight class="mobile-service-menu__arrow" />
              </div>
            </template>
          </ContactPopup>

          <ConsultantPopup>
            <template #trigger>
              <div class="mobile-service-menu__item">
                <SvgIcon class="mobile-service-menu__icon" name="consultant" size="20" />
                <span class="mobile-service-menu__content">
                  <span class="mobile-service-menu__title">专属顾问</span>
                  <span class="mobile-service-menu__desc">添加专属顾问，免费获取多重权益</span>
                </span>
                <IconRight class="mobile-service-menu__arrow" />
              </div>
            </template>
          </ConsultantPopup>

          <button type="button" class="mobile-service-menu__item" @click="handleFeedbackClick">
            <SvgIcon class="mobile-service-menu__icon" name="feedback" size="20" />
            <span class="mobile-service-menu__content">
              <span class="mobile-service-menu__title">意见反馈</span>
              <span class="mobile-service-menu__desc">您的建议让我们做得更好</span>
            </span>
            <IconRight class="mobile-service-menu__arrow" />
          </button>
        </div>
      </MobilePopup>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue'
import { IconRight } from '@arco-design/web-vue/es/icon'
import BaseBackToTop from '../BackToTop.vue'
import ConsultantPopup from '../consultant/ConsultantWrapper.vue'
import ContactPopup from '../contact/ContactPopupWrapper.vue'
import MobilePopup from '~/components/mobile/Popup.vue'
import { openFeedbackModal } from '~/utils/feedbackModal'

const { withBase } = useBasePath()

const FLOATING_BOTTOM = 128
const FLOATING_RIGHT = 8
const FLOATING_HEIGHT = 88
const FLOATING_AVATAR_HEIGHT = 40
const MIN_MARGIN_TOP = 60
const MIN_MARGIN_BOTTOM = 8
const DRAG_MOVE_THRESHOLD = 3
const DRAG_CLICK_SUPPRESS_MS = 250
const BACK_TO_TOP_VIEWPORT_RATIO = 1.5

const menuVisible = ref(false)
const isDragging = ref(false)
const dragMoved = ref(false)
const suppressClickUntil = ref(0)
const showBackToTop = ref(false)

const floatingRef = ref(null)

const position = reactive({
  y: 0,
})

const dragStart = reactive({
  y: 0,
})

const floatingPositionStyle = computed(() => ({
  right: `${FLOATING_RIGHT}px`,
  transform: `translate3d(0, ${position.y}px, 0)`,
}))

const shouldSuppressClick = () => Date.now() < suppressClickUntil.value
const canUseFloatingAction = () => !shouldSuppressClick()

const getFloatingHeight = () =>
  floatingRef.value?.offsetHeight ||
  (showBackToTop.value ? FLOATING_HEIGHT : FLOATING_AVATAR_HEIGHT)

const updatePositionWithConstraints = () => {
  if (!import.meta.client) return

  const windowHeight = window.innerHeight
  const floatingHeight = getFloatingHeight()
  const minTop = MIN_MARGIN_TOP
  const maxTop = Math.max(minTop, windowHeight - floatingHeight - MIN_MARGIN_BOTTOM)

  position.y = Math.min(Math.max(position.y, minTop), maxTop)
}

const initPosition = () => {
  if (!import.meta.client) return
  position.y = window.innerHeight - FLOATING_BOTTOM - getFloatingHeight()
  updatePositionWithConstraints()
}

const showMenu = () => {
  if (shouldSuppressClick()) return
  menuVisible.value = true
}

const hideMenu = () => {
  menuVisible.value = false
}

const handleScroll = () => {
  if (!import.meta.client) return

  const shouldShow = window.scrollY > window.innerHeight * BACK_TO_TOP_VIEWPORT_RATIO
  if (shouldShow === showBackToTop.value) return

  showBackToTop.value = shouldShow
  nextTick(updatePositionWithConstraints)
}

const handleFeedbackClick = () => {
  hideMenu()
  openFeedbackModal()
}

const beginDrag = (clientY) => {
  isDragging.value = true
  dragMoved.value = false
  dragStart.y = clientY
}

const startDrag = (event) => {
  if (menuVisible.value || (event.button !== undefined && event.button !== 0)) return

  event.stopPropagation()
  beginDrag(event.clientY)
}

const startTouchDrag = (event) => {
  if (menuVisible.value) return

  event.stopPropagation()
  beginDrag(event.touches[0].clientY)
}

const onDragMove = (clientY) => {
  if (!isDragging.value) return

  const deltaY = clientY - dragStart.y
  if (Math.abs(deltaY) > DRAG_MOVE_THRESHOLD) {
    dragMoved.value = true
  }

  position.y += deltaY
  updatePositionWithConstraints()
  dragStart.y = clientY
}

const onMouseMove = (event) => {
  if (!isDragging.value) return
  event.stopPropagation()
  event.preventDefault()
  onDragMove(event.clientY)
}

const onTouchMove = (event) => {
  if (!isDragging.value) return
  event.stopPropagation()
  event.preventDefault()
  onDragMove(event.touches[0].clientY)
}

const stopDrag = (event) => {
  if (!isDragging.value) return
  event?.stopPropagation?.()

  isDragging.value = false
  if (dragMoved.value) {
    suppressClickUntil.value = Date.now() + DRAG_CLICK_SUPPRESS_MS
  }
}

const handleResize = () => {
  handleScroll()
  updatePositionWithConstraints()
}

onMounted(() => {
  if (!import.meta.client) return

  requestAnimationFrame(() => {
    initPosition()
    handleScroll()
  })
  window.addEventListener('resize', handleResize, { passive: true })
  window.addEventListener('scroll', handleScroll, { passive: true })
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', stopDrag)
  document.addEventListener('touchmove', onTouchMove, { passive: false })
  document.addEventListener('touchend', stopDrag)
  document.addEventListener('touchcancel', stopDrag)
})

onUnmounted(() => {
  if (!import.meta.client) return

  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', stopDrag)
  document.removeEventListener('touchmove', onTouchMove)
  document.removeEventListener('touchend', stopDrag)
  document.removeEventListener('touchcancel', stopDrag)
})
</script>

<style lang="scss" scoped>
.customer-service-bot-mobile {
  position: fixed;
  inset: 0;
  z-index: $z-index-page-floating;
  pointer-events: none;
}

.mobile-floating-service {
  position: absolute;
  width: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
  top: 0;
  transition: transform 0.2s ease;
  touch-action: none;

  &.is-dragging {
    transition: none;
  }

  button {
    padding: 0;
    border: 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  &__avatar {
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    background: transparent;
    box-shadow: 0 3.2px 8px rgba($color-black, 0.1);

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border: 0.8px solid $bg-white;
      border-radius: 50%;
      pointer-events: none;
    }

    img {
      width: 40px;
      height: 40px;
      display: block;
      object-fit: cover;
    }
  }

  &__back-top {
    box-shadow: 0 3.2px 8px rgba($color-black, 0.1);
  }
}

.mobile-service-menu {
  display: flex;
  flex-direction: column;
  padding: 0;

  :deep(.contact-popup-wrapper),
  :deep(.customer-service-popup-wrapper) {
    display: block;
    width: 100%;
  }

  &__item {
    position: relative;
    display: flex;
    align-items: flex-start;
    width: 100%;
    padding: 16px 0;
    border: 0;
    border-bottom: 0.5px solid rgba($color-black, 0.12);
    background: transparent;
    color: inherit;
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background-color: $bg-soft-color;
    }
  }

  &__icon {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    margin: 2px 12px 0 0;
    color: #202427;
  }

  &__content {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: #202427;
  }

  &__desc {
    font-size: 14px;
    line-height: 22px;
    color: #4f565f;
  }

  &__arrow {
    width: 16px;
    height: 16px;
    flex: 0 0 16px;
    margin: 4px 0 0 16px;
    color: #a1a1a6;
  }
}
</style>
